import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Col,
  Card,
  CardBody,
  CardFooter,
  CardImg,
  Row,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { getDoc, doc, deleteDoc } from "firebase/firestore";
import { firestore } from "../../firebase";

import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import PlateFormat from "../../components/PlateFormat";
import AIChatButton from "../../components/Common/AIChatButton";
import styles from "../Anpr/styles.module.css";

/** Color map por tipo de ticket (hex custom para evitar conflicto con tema Upzet) */
const TYPE_CONFIG = {
  hour: { bg: "#3b82f6", label: "xHora" },
  stay: { bg: "#22c55e", label: "xEstadía" },
  tn: { bg: "#ef4444", label: "xTurno" },
  // El desktop graba type='plana' desde v2.0.5; sin esta entrada el ticket
  // se mostraba como "--". Color alineado con el Tarifario.
  plana: { bg: "#a569bd", label: "xPlana" },
};

const DEFAULT_TYPE = { bg: "#6b7280", label: "--" };
const getTypeConfig = (type) => TYPE_CONFIG[type] || DEFAULT_TYPE;

function getPermanencia(initDate) {
  const now = moment();
  const start = moment(initDate);
  const diff = moment.duration(now.diff(start));
  const days = diff.days();
  const hours = diff.hours();
  const minutes = diff.minutes();

  if (days >= 1) {
    return `${days}d ${hours}:${minutes.toString().padStart(2, "0")} hs.`;
  }
  return `${hours}:${minutes.toString().padStart(2, "0")} hs.`;
}

const Tickets = (props) => {
  const customer_id = props.currentCompany.id;
  const country = props.currentCompany.country;
  const openedTickets = useSelector((state) => state.data.openedTickets);
  const hasAnpr = props.appConfig?.modules?.anpr === 1;

  const [modalOpenDelete, setModalOpenDelete] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const openDeleteModal = (ticket) => {
    setTicketToDelete(ticket);
    setModalOpenDelete(true);
  };

  const handleDelete = async () => {
    setModalOpenDelete(false);
    await deleteTicket(ticketToDelete.id);
  };

  const deleteTicket = async (ticketId) => {
    try {
      const ticketRef = doc(firestore, "users", customer_id, "tickets", String(ticketId));
      const ticketSnap = await getDoc(ticketRef);
      if (ticketSnap.exists()) {
        await deleteDoc(ticketRef);
        return true;
      }
      return null;
    } catch (error) {
      console.error("Error al eliminar el ticket:", error);
    }
  };

  if (!Array.isArray(openedTickets) || !openedTickets.length) {
    return (
      <div className="text-center text-muted py-5">
        <span className="material-symbols-rounded d-block mb-3" style={{ fontSize: 48, opacity: 0.3 }}>directions_car</span>
        No hay tickets abiertos
      </div>
    );
  }

  return (
    <>
      {/* Header: contador + botón editar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted" style={{ fontSize: 13 }}>
          {openedTickets.length} ticket{openedTickets.length !== 1 ? "s" : ""} abierto{openedTickets.length !== 1 ? "s" : ""}
        </span>
        <Button
          color={editMode ? "danger" : "light"}
          size="sm"
          outline={!editMode}
          onClick={() => setEditMode(!editMode)}
        >
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>{editMode ? "close" : "edit"}</span>
          {editMode ? "Cancelar" : "Editar"}
        </Button>
      </div>

      {editMode && (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 12 }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>info</span>
          En caso de error de sincronización, puede eliminar tickets sin necesidad de resincronizar.
          Esta acción solo afecta a Monitor.
        </div>
      )}

      {/* ===== DESKTOP CARDS (≥md) ===== */}
      <Row className="g-3 d-none d-md-flex">
        {openedTickets.map((item, index) => {
          const cfg = getTypeConfig(item.type);
          const plate = PlateFormat(item.plate, country);
          const imgSrc = item.picture ? `data:image/png;base64,${item.picture}` : null;

          return (
            <Col md={4} lg={4} xl={3} xxl={2} key={index}>
              <Card
                className="shadow-sm h-100"
                style={{ borderLeft: `4px solid ${cfg.bg}`, borderRadius: 8, overflow: "hidden", cursor: "pointer" }}
                onClick={() => setSelectedTicket(item)}
              >
                {/* Imagen + placa overlay (solo si tiene ANPR) */}
                {hasAnpr && (
                  <div className={styles["image-container"]}>
                    {imgSrc ? (
                      <CardImg
                        top
                        src={imgSrc}
                        alt={item.plate}
                        className={styles["card-img"]}
                      />
                    ) : (
                      <div className={styles["no-image-placeholder"]}>
                        <span className="material-symbols-rounded">directions_car</span>
                      </div>
                    )}
                    <div className={styles["plate-overlay"]}>{plate}</div>
                  </div>
                )}

                {/* Body */}
                <CardBody className={styles["card-body"]}>
                  {!hasAnpr && (
                    <div className="text-center mb-1" style={{ fontSize: 16, fontWeight: 800, letterSpacing: 1 }}>
                      {plate}
                    </div>
                  )}
                  <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                    <Badge color="dark" style={{ fontSize: 12 }}>{item.category_name}</Badge>
                    <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>
                      {cfg.label}
                    </Badge>
                    {item.is_reservation ? (
                      <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>
                        <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>bookmark</span>Reserva
                      </Badge>
                    ) : null}
                  </div>

                  {item.car_detail && (
                    <div className="text-muted text-center mt-1" style={{ fontSize: 12 }}>
                      {item.car_detail}
                    </div>
                  )}

                  {(item.place_name || item.floor_name) && (
                    <div className="text-center mt-1" style={{ fontSize: 12, color: "#6c757d" }}>
                      <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>location_on</span>
                      {item.place_name}{item.floor_name ? ` - ${item.floor_name}` : ""}
                    </div>
                  )}

                  {item.obs && (
                    <div className="text-center mt-1">
                      <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>
                        <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>sticky_note_2</span>{item.obs}
                      </Badge>
                    </div>
                  )}
                </CardBody>

                {/* Footer */}
                <CardFooter className={styles["card-footer"]}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div>{moment(item.init_date).format("DD/MM HH:mm")} — <b>{getPermanencia(item.init_date)}</b></div>
                      <div style={{ fontSize: "0.65rem" }}>{item.fullName}</div>
                    </div>
                    {editMode && (
                      <Button color="danger" size="sm" className="py-0 px-1" onClick={(e) => { e.stopPropagation(); openDeleteModal(item); }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>delete</span>
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* ===== MOBILE CARDS (<md) ===== */}
      <div className="d-md-none">
        {openedTickets.map((item, index) => {
          const cfg = getTypeConfig(item.type);
          const plate = PlateFormat(item.plate, country);
          const imgSrc = item.picture ? `data:image/png;base64,${item.picture}` : null;

          return (
            <Card
              key={index}
              className="mb-2 shadow-sm"
              style={{ borderLeft: `4px solid ${cfg.bg}`, borderRadius: 8, overflow: "hidden", cursor: "pointer" }}
              onClick={() => setSelectedTicket(item)}
            >
              <div className={styles["card-mobile"]}>
                {/* Thumbnail (solo si tiene ANPR) */}
                {hasAnpr && (
                  imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={item.plate}
                      className={styles["card-mobile-img"]}
                    />
                  ) : (
                    <div className={styles["card-mobile-no-img"]}>
                      <span className="material-symbols-rounded">directions_car</span>
                    </div>
                  )
                )}

                {/* Info */}
                <div className={styles["card-mobile-body"]}>
                  <div className="d-flex align-items-center justify-content-between">
                    <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: 0.5 }}>{plate}</span>
                    {editMode && (
                      <Button color="danger" size="sm" className="py-0 px-1" onClick={(e) => { e.stopPropagation(); openDeleteModal(item); }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>delete</span>
                      </Button>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-1">
                    <Badge color="dark" style={{ fontSize: 12 }}>{item.category_name}</Badge>
                    <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>
                    {item.is_reservation ? (
                      <Badge color="warning" className="text-dark" style={{ fontSize: 12 }}>Reserva</Badge>
                    ) : null}
                  </div>
                  {item.car_detail && (
                    <div className="text-muted" style={{ fontSize: 13 }}>{item.car_detail}</div>
                  )}
                  {item.obs && (
                    <div className="mt-1">
                      <Badge color="warning" className="text-dark" style={{ fontSize: 12 }}>
                        <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>sticky_note_2</span>{item.obs}
                      </Badge>
                    </div>
                  )}
                  <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                    {moment(item.init_date).format("DD/MM HH:mm")} — <b>{getPermanencia(item.init_date)}</b>
                    <span className="ms-2" style={{ fontSize: 12 }}>{item.fullName}</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Modal: confirmar eliminación */}
      <ConfirmDeleteModal
        isOpen={modalOpenDelete}
        toggle={() => setModalOpenDelete(!modalOpenDelete)}
        onConfirm={handleDelete}
        ticket={ticketToDelete}
        country={country}
      />

      {/* Modal: ficha completa del ticket */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        toggle={() => setSelectedTicket(null)}
        country={country}
        hasAnpr={hasAnpr}
      />

      {/* AI Chat */}
      <TicketsAI tickets={openedTickets} />
    </>
  );
};

const TicketDetailModal = ({ ticket, isOpen, toggle, country, hasAnpr }) => {
  if (!ticket) return null;
  const cfg = getTypeConfig(ticket.type);
  const plate = PlateFormat(ticket.plate, country);
  const imgSrc = ticket.picture ? `data:image/png;base64,${ticket.picture}` : null;
  const editLog = (() => { try { return Array.isArray(ticket.edit_log) ? ticket.edit_log : JSON.parse(ticket.edit_log || '[]') } catch { return [] } })();

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody className="p-0">
        {/* Imagen (solo si ANPR y tiene foto) */}
        {hasAnpr && imgSrc && (
          <img
            src={imgSrc}
            alt={ticket.plate}
            style={{ width: "100%", maxHeight: "50vh", objectFit: "cover" }}
          />
        )}

        {/* Datos */}
        <div className="p-3">
          {/* Placa + badges */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>{plate}</span>
            <div className="d-flex gap-1">
              <Badge color="dark" style={{ fontSize: 12 }}>{ticket.category_name}</Badge>
              <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>
              {ticket.is_reservation ? (
                <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>Reserva</Badge>
              ) : null}
            </div>
          </div>

          {/* Detalle del vehículo */}
          {ticket.car_detail && (
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>directions_car</span>{ticket.car_detail}
            </div>
          )}

          {/* Ubicación */}
          {(ticket.place_name || ticket.floor_name) && (
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>location_on</span>
              {ticket.place_name}{ticket.floor_name ? ` - ${ticket.floor_name}` : ""}
            </div>
          )}

          {/* Observaciones */}
          {ticket.obs && (
            <div className="mb-2">
              <Badge color="warning" className="text-dark" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>sticky_note_2</span>{ticket.obs}
              </Badge>
            </div>
          )}

          <hr className="my-2" />

          {/* Tiempo */}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div style={{ fontSize: 13 }}>
                <span className="material-symbols-rounded me-1 text-muted" style={{ fontSize: 16 }}>schedule</span>
                {moment(ticket.init_date).format("DD/MM/YYYY HH:mm")} — <b>{getPermanencia(ticket.init_date)}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>person</span>{ticket.fullName}
              </div>
            </div>
            {ticket.id && (
              <span className="text-muted" style={{ fontSize: 11 }}>#{ticket.id}</span>
            )}
          </div>

          {/* Edit Log */}
          {editLog.length > 0 && (<>
            <hr className="my-2" />
            <div style={{ fontSize: 12 }}>
              <div className="d-flex align-items-center gap-1 mb-2">
                <span className="material-symbols-rounded" style={{ fontSize: 16, color: "#888" }}>edit_note</span>
                <span style={{ fontWeight: 600 }}>Historial de ediciones</span>
              </div>
              {editLog.map((entry, i) => {
                const labels = { plate: "Patente", type: "Tipo", category: "Categoría", category_id: "Categoría", car_detail: "Vehículo", deal: "Acuerdo" };
                const dateStr = (entry.date || "").replace(/(\d{4})-(\d{2})-(\d{2})/, "$3/$2/$1").substring(0, 16);
                return (
                  <div key={i} style={{ marginBottom: i < editLog.length - 1 ? 8 : 0 }}>
                    {i > 0 && <hr className="my-1" style={{ opacity: 0.15 }} />}
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {dateStr} — <strong>{entry.user || "?"}</strong>
                    </div>
                    {Object.entries(entry.changes || {}).map(([key, vals]) => (
                      <div key={key} className="text-muted" style={{ fontSize: 12 }}>
                        {labels[key] || key}: {vals[0] || "-"} → {vals[1] || "-"}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>)}
        </div>
      </ModalBody>
    </Modal>
  );
};

const ConfirmDeleteModal = ({ ticket, isOpen, toggle, onConfirm, country }) => {
  if (!ticket) return null;
  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Confirmar eliminación</ModalHeader>
      <ModalBody>
        ¿Realmente quieres eliminar el ticket con matrícula{" "}
        <b>{PlateFormat(ticket.plate, country)}</b>?
        <br /><br />
        <small className="text-muted">
          Esta acción se aplica solo a TicketControl Monitor y no afecta a TicketControl.
        </small>
      </ModalBody>
      <ModalFooter>
        <Button color="danger" onClick={onConfirm}>Sí, eliminar</Button>
        <Button color="secondary" onClick={toggle}>Cancelar</Button>
      </ModalFooter>
    </Modal>
  );
};

// ---- AI Data Summary for opened tickets ----
const TicketsAI = ({ tickets }) => {
  const aiDataSummary = useMemo(() => {
    if (!tickets || !tickets.length) return "";

    let out = `TICKETS ABIERTOS (${tickets.length} en total)\n\n`;

    // By type
    const byType = {};
    tickets.forEach(t => {
      const label = TYPE_CONFIG[t.type]?.label || "Otro";
      byType[label] = (byType[label] || 0) + 1;
    });
    out += `POR TIPO:\n`;
    Object.entries(byType).forEach(([k, v]) => {
      const pct = ((v / tickets.length) * 100).toFixed(0);
      out += `- ${k}: ${v} (${pct}%)\n`;
    });
    out += "\n";

    // By category
    const byCategory = {};
    tickets.forEach(t => {
      const cat = t.category_name || "Sin categoria";
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });
    if (Object.keys(byCategory).length > 1) {
      out += `POR CATEGORIA:\n`;
      Object.entries(byCategory).sort((a, b) => b[1] - a[1]).forEach(([k, v]) => {
        out += `- ${k}: ${v}\n`;
      });
      out += "\n";
    }

    // Permanence analysis
    const now = moment();
    const permanences = tickets.map(t => {
      const start = moment(t.init_date);
      return now.diff(start, "minutes");
    });
    const avgMinutes = permanences.reduce((a, b) => a + b, 0) / permanences.length;
    const maxMinutes = Math.max(...permanences);
    const formatDuration = (mins) => {
      const h = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    out += `PERMANENCIA:\n`;
    out += `- Promedio: ${formatDuration(avgMinutes)}\n`;
    out += `- Mayor permanencia: ${formatDuration(maxMinutes)}\n`;

    // Tickets with longest stay
    const longStay = tickets
      .map(t => ({ ...t, mins: now.diff(moment(t.init_date), "minutes") }))
      .sort((a, b) => b.mins - a.mins)
      .slice(0, 5);
    out += `\nTICKETS CON MAYOR PERMANENCIA:\n`;
    longStay.forEach(t => {
      out += `- ${t.plate || "Sin matricula"} | ${formatDuration(t.mins)} | ${t.category_name || "-"} | Ingreso: ${moment(t.init_date).format("DD/MM HH:mm")}\n`;
    });
    out += "\n";

    // By operator
    const byOperator = {};
    tickets.forEach(t => {
      const op = t.fullName || "Sin operador";
      byOperator[op] = (byOperator[op] || 0) + 1;
    });
    if (Object.keys(byOperator).length > 1) {
      out += `POR OPERADOR:\n`;
      Object.entries(byOperator).sort((a, b) => b[1] - a[1]).forEach(([k, v]) => {
        out += `- ${k}: ${v} tickets\n`;
      });
      out += "\n";
    }

    // Reservations
    const reservations = tickets.filter(t => t.is_reservation);
    if (reservations.length) {
      out += `RESERVAS: ${reservations.length} de ${tickets.length} tickets son reservas\n\n`;
    }

    // By floor/location
    const byLocation = {};
    tickets.forEach(t => {
      if (t.floor_name || t.place_name) {
        const loc = [t.place_name, t.floor_name].filter(Boolean).join(" - ");
        byLocation[loc] = (byLocation[loc] || 0) + 1;
      }
    });
    if (Object.keys(byLocation).length > 1) {
      out += `POR UBICACION:\n`;
      Object.entries(byLocation).sort((a, b) => b[1] - a[1]).forEach(([k, v]) => {
        out += `- ${k}: ${v}\n`;
      });
      out += "\n";
    }

    // Hourly distribution of entries
    const byHour = {};
    tickets.forEach(t => {
      if (t.init_date) {
        const h = new Date(t.init_date).getHours();
        byHour[h] = (byHour[h] || 0) + 1;
      }
    });
    const hours = Object.keys(byHour).map(Number).sort((a, b) => a - b);
    if (hours.length >= 3) {
      out += `DISTRIBUCION HORARIA DE INGRESOS:\n`;
      hours.forEach(h => {
        out += `- ${String(h).padStart(2, "0")}:00: ${byHour[h]} tickets\n`;
      });
    }

    return out;
  }, [tickets]);

  const aiSuggestions = useMemo(() => [
    "¿Cuantos tickets hay abiertos?",
    "¿Cual es la permanencia promedio?",
    "¿Que vehiculo lleva mas tiempo?",
    "¿Como se distribuyen por tipo?",
    "¿Que operador ingreso mas vehiculos?",
    "¿Hay reservas activas?",
  ], []);

  if (!aiDataSummary) return null;
  return (
    <AIChatButton
      context="tickets"
      dataSummary={aiDataSummary}
      suggestions={aiSuggestions}
      title="Asistente Tickets"
    />
  );
};

const mapStateToProps = (state) => ({
  openedTickets: state.data.openedTickets,
  currentCompany: state.companies.currentCompany,
  appConfig: state.data.appConfig,
});

export default connect(mapStateToProps)(Tickets);
